/**
 * AASB 1058 — Income of Not-for-Profit Entities. Rulepack v1.0.0.
 * Determines whether a transaction falls under AASB 1058 (immediate recognition) or
 * AASB 15 (over-time recognition) based on enforceability and specific obligations.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseBool, parseDate, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb1058-1.0.0';
const fb = findingBuilder('AASB_1058' as any, VERSION);

const HMAP = makeHeaderMap([
  ['transaction_id', 'transaction id', 'id', 'grant id'],
  ['income_type', 'income type', 'type', 'category'],
  ['amount', 'amount', 'value', 'income amount'],
  ['has_specific_obligations', 'specific obligations', 'has obligations', 'sufficiently specific'],
  ['enforceable', 'enforceable', 'is enforceable'],
  ['recognition_basis', 'recognition basis', 'basis', 'standard applied'],
  ['recognised_to_date', 'recognised', 'recognised to date'],
  ['unspent_balance', 'unspent', 'unspent balance', 'deferred income'],
  ['period_start', 'period start', 'grant start'],
  ['period_end', 'period end', 'grant end'],
]);

const REQUIRED = ['transaction_id', 'income_type', 'amount', 'has_specific_obligations', 'enforceable'];

export function detectAASB1058(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  const boost = ['grant', 'donation', 'enforceable', 'obligations'].some(k => headers.some(h => String(h ?? '').toLowerCase().includes(k)));
  return { confidence: Math.min(1, (matched.length / REQUIRED.length) * (boost ? 1.5 : 1)), matched, missing: REQUIRED.filter(r => !hits.has(r)) };
}

export function validateAASB1058(headers: string[], data: CellValue[][], asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_1058' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('transaction_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB1058.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const amount = parseNumber(get('amount'))!;
    const hasObligations = parseBool(get('has_specific_obligations'));
    const enforceable = parseBool(get('enforceable'));
    const basis = String(get('recognition_basis') ?? '').toLowerCase().trim();
    const recognised = idx.recognised_to_date !== undefined ? parseNumber(get('recognised_to_date')) : null;
    const unspent = idx.unspent_balance !== undefined ? parseNumber(get('unspent_balance')) : null;
    const start = idx.period_start !== undefined ? parseDate(get('period_start')) : null;
    const end = idx.period_end !== undefined ? parseDate(get('period_end')) : null;

    // R02 — determine correct standard
    const aasb15Applies = hasObligations && enforceable;
    const expectedBasis = aasb15Applies ? 'aasb15' : 'aasb1058';

    if (basis && !basis.includes(expectedBasis.replace('aasb', 'aasb '))) {
      // Allow 'aasb_15', 'aasb15', '15' etc
      const matchesExpected = (basis.includes('15') && aasb15Applies) || (basis.includes('1058') && !aasb15Applies);
      if (!matchesExpected) {
        findings.push(fb.build('AASB1058.R02', 'warning', 'Recognition basis may not match enforceability + obligations',
          `Transaction ${id}: hasObligations=${hasObligations}, enforceable=${enforceable} — should fall under ${aasb15Applies ? 'AASB 15 (over-time as obligations performed)' : 'AASB 1058 (immediate recognition)'}. Reported basis: "${basis}".`,
          { rowId: id, evidence: { hasObligations, enforceable, basis, expected: expectedBasis } }));
      }
    } else if (!basis) {
      findings.push(fb.build('AASB1058.R02', 'info', 'Recognition basis not stated',
        `Transaction ${id}: should be ${expectedBasis === 'aasb15' ? 'AASB 15' : 'AASB 1058'} per obligations + enforceability.`,
        { rowId: id }));
    }

    // R03 — over-time recognition for AASB 15: time-elapsed proxy
    if (aasb15Applies && start && end && recognised !== null) {
      const totalDays = (end.getTime() - start.getTime()) / 86400000;
      const elapsed = Math.max(0, Math.min(totalDays, (asAt.getTime() - start.getTime()) / 86400000));
      const expected = totalDays > 0 ? amount * (elapsed / totalDays) : 0;
      const tol = Math.max(50, 0.10 * amount);
      if (Math.abs(recognised - expected) > tol) {
        findings.push(fb.build('AASB1058.R03', 'warning', 'AASB 15 recognised differs from time-elapsed proxy',
          `Transaction ${id}: recognised $${fmt(recognised)}, time-elapsed proxy $${fmt(expected)} (${(elapsed / totalDays * 100).toFixed(1)}% elapsed). If output / input measure differs from time, document.`,
          { rowId: id }));
      }
    }

    // R04 — unspent grant balance reconciliation
    if (recognised !== null && unspent !== null) {
      const expectedUnspent = Math.max(0, amount - recognised);
      const tol = Math.max(50, 0.005 * amount);
      if (Math.abs(unspent - expectedUnspent) > tol) {
        findings.push(fb.build('AASB1058.R04', 'warning', 'Unspent balance does not reconcile to amount − recognised',
          `Transaction ${id}: unspent $${fmt(unspent)}, expected $${fmt(expectedUnspent)} (amount $${fmt(amount)} − recognised $${fmt(recognised)}).`,
          { rowId: id }));
      }
    }

    // R05 — AASB 1058 recognition is immediate
    if (!aasb15Applies && recognised !== null && Math.abs(recognised - amount) > Math.max(50, 0.005 * amount) && (asAt.getTime() - (start?.getTime() ?? 0)) > 30 * 86400000) {
      findings.push(fb.build('AASB1058.R05', 'info', 'AASB 1058 immediate recognition — partial recognition unusual',
        `Transaction ${id}: AASB 1058 typically recognises income immediately. Reported $${fmt(recognised)} of $${fmt(amount)} — confirm rationale (e.g. capital grant for asset construction may defer until conditions met).`,
        { rowId: id }));
    }
  });

  return {
    summary: { standard: 'AASB_1058' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
